import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Paginator } from "@components";
import { useDebounce } from "@hooks";
import { Notification } from "@rio-cloud/rio-uikit";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import RadioButton from "@rio-cloud/rio-uikit/RadioButton";
import Select from "@rio-cloud/rio-uikit/Select";
import Slider from "@rio-cloud/rio-uikit/Slider";
import SubNavigation from "@rio-cloud/rio-uikit/SubNavigation";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import { createKmInputHandlers, createTimeInputTextHandlers } from "../ModalMapInterestArea";

import CustomInputSelect from "../../components/CustomInputSelect/CustomInputSelect";
import { useMapDrawingContext } from "../../providers/MapDrawingContext";
import { createAreaRework, getAllAreas } from "../../services/areas";
import { CreateAreaReworkPayload } from "../../types/areas";
import ChipStatus from "../ChipStatus";

import {
  defineAreas,
  defineAreasRework,
  defineAreasSelectedIds,
  defineAvoidAreas,
} from "../../store/routes/RoutesSlice";

import { RootState, useAppSelector } from "@store";
import { fetchAutosuggest } from "src/services/routes";
import Autosuggest from "../Autosuggest";
import { ICoordinates, ISandboxMarker } from "../Map/interfaces";
import { formatScope } from "../TableAreas";
import { formatAreaIds, formatCircle, formatPolygon, sortStr } from "@utils";
import { MarkerColor } from "@rio-cloud/rio-uikit/mapTypes";

const PAGE_SIZE = 10;
const SIZE_ITEMS = 10000;

interface Props {
  onCancel: (shouldRefresh?: boolean) => void;
  pages: number;
  initialData?: CreateAreaReworkPayload[];
  onDataUpdate?: (data: CreateAreaReworkPayload[], pages: number) => void;
}

export const calculateOffsetCoordinates = (lat: number, lng: number, distanceMeters: number, bearingDegrees = 0) => {
  const R = 6378137;
  const brng = (bearingDegrees * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lng * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distanceMeters / R) + Math.cos(lat1) * Math.sin(distanceMeters / R) * Math.cos(brng)
  );

  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(distanceMeters / R) * Math.cos(lat1),
      Math.cos(distanceMeters / R) - Math.sin(lat1) * Math.sin(lat2)
    );

  return {
    lat: (lat2 * 180) / Math.PI,
    lng: (lon2 * 180) / Math.PI,
  };
};

export const cleanNumeric = (value?: any): number => {
  if (value == null) return 0;

  const stringValue = typeof value === "number" ? value.toString() : value;
  const numeric = stringValue.replace(/[^\d.,-]/g, "").replace(",", ".");
  const parsed = parseFloat(numeric);
  return isNaN(parsed) ? 0 : parsed;
};

const DynamicContentArea = ({ onCancel, initialData, onDataUpdate }: Props) => {
  const [searchParams] = useSearchParams();
  const routeId = searchParams.get("id");

  const isFirstRenderRef = useRef(true);

  if (isFirstRenderRef.current) {
    isFirstRenderRef.current = false;
  }

  const {
    radius,
    handleChangeRadius,
    drawingType,
    setDrawingType,
    drawingPoints,
    setDrawingPoints,
    type,
    setType,
    formattedAddress,
    setFormattedAddress,
    shape,
    setShape,
    selectedOption,
    setSelectedOption,
    setDrawingMarkers,
    setEnableDrawingClick,
    setEnableContextMenuClick,
    circleDrawing,
    polygonDrawing,
    setCircleDrawing,
    setPolygonDrawing,
    setCenterMarker,
    setMapCenter,
  } = useMapDrawingContext();

  const dispatch = useDispatch();

  const { areasRework } = useAppSelector((state) => state.routes);
  const areasSelectedIdsFromStore = useAppSelector((state) => state.routes.areasSelectedIds);
  const avoidAreas = useSelector((state: RootState) => state.routes.areasToAvoid);
  const [manualAvoidFormatted, setManualAvoidFormatted] = useState<string[]>([]);
  const [checkboxAvoidFormatted, setCheckboxAvoidFormatted] = useState<string[]>([]);
  const [currentDrawingArea, setCurrentDrawingArea] = useState<string>("");
  const [areas, setAreas] = useState<CreateAreaReworkPayload[]>([]);
  const [selectedAreaCache, setSelectedAreaCache] = useState<Record<string, CreateAreaReworkPayload>>({});
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [selectedMinPermanencyTimeOption, setSelectedMinPermanencyTimeOption] = useState("HOUR");
  const [selectedMaxPermanencyTimeOption, setSelectedMaxPermanencyTimeOption] = useState("HOUR");
  const [minKmInputValue, setMinKmInputValue] = useState("0");
  const [maxKmInputValue, setMaxKmInputValue] = useState("0");
  const [minTimeInputValue, setMinTimeInputValue] = useState("0");
  const [maxTimeInputValue, setMaxTimeInputValue] = useState("0");
  const [areaName, setAreaName] = useState("");
  const [areaNarration, setAreaNarration] = useState("");
  const [createdAreaId, setCreatedAreaId] = useState<number | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [expectedPageData, setExpectedPageData] = useState<{ page: number; data: CreateAreaReworkPayload[] } | null>(
    null
  );
  const hasInitialLoadRef = useRef(false);
  const isUpdatingAreasRef = useRef(false);
  const isLoadingDataRef = useRef(false);
  const [preSelectedAreaIds, setPreSelectedAreaIds] = useState<string[]>([]);
  const [preSelectedAreaCache, setPreSelectedAreaCache] = useState<Record<string, CreateAreaReworkPayload>>({});
  const [userDeselectedIds, setUserDeselectedIds] = useState<string[]>([]);
  const [userSelectedIds, setUserSelectedIds] = useState<string[]>([]);
  const hasComponentInitializedRef = useRef(false);
  const hasUserInteractedRef = useRef(false);
  const [previousPage, setPreviousPage] = useState<number>(1);
  const isPaginatorChangeRef = useRef(false);
  const lastProcessedAreasRef = useRef<string>("");
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = searchParams.get("tab") || "1";
  const [controller, setController] = useState({
    start: 0,
    end: 10,
  });
  const [pages, setPages] = useState(1);

  const hasDetectedMapClickRef = useRef(false);

  const getConsolidatedSelectedIds = useCallback(() => {
    const allAreaIds = [
      ...new Set([...preSelectedAreaIds, ...userSelectedIds, ...selectedAreaIds, ...areasSelectedIdsFromStore]),
    ].filter((id) => !userDeselectedIds.includes(id));

    return allAreaIds.map((id) => (typeof id === "string" ? parseInt(id, 10) : id)).filter((id) => !isNaN(id));
  }, [preSelectedAreaIds, userSelectedIds, selectedAreaIds, areasSelectedIdsFromStore, userDeselectedIds]);

  useEffect(() => {
    if (drawingPoints.length > 0 && !hasDetectedMapClickRef.current) {
      hasUserInteractedRef.current = true;
      hasDetectedMapClickRef.current = true;
    }

    if (drawingPoints.length === 0) {
      hasDetectedMapClickRef.current = false;
    }
  }, [drawingPoints.length]);

  const [isMaxVelocity, setIsMaxVelocity] = useState(false);
  const [isMaxTime, setIsMaxTime] = useState(false);

  const kmInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);

  const minKmHandlers = createKmInputHandlers(setMinKmInputValue, "", () => { });
  const maxKmHandlers = createKmInputHandlers(setMaxKmInputValue, minKmInputValue, setIsMaxVelocity, kmInputRef);

  const minTimeInputHandlers = useMemo(() => {
    return createTimeInputTextHandlers(
      setMinTimeInputValue,
      selectedMinPermanencyTimeOption,
      maxTimeInputValue,
      setIsMaxTime,
      timeInputRef,
      false
    );
  }, [selectedMinPermanencyTimeOption, maxTimeInputValue]);

  const maxTimeInputHandlers = createTimeInputTextHandlers(
    setMaxTimeInputValue,
    selectedMaxPermanencyTimeOption,
    minTimeInputValue,
    setIsMaxTime,
    timeInputRef,
    true
  );

  const shapeOptions = [
    { value: "CIRCLE", label: "Área circular" },
    { value: "POLYGON", label: "Área poligonal" },
  ];

  const typeOptions = [
    { value: "INTEREST", label: "Área de interesse" },
    { value: "AVOID", label: "Área a ser evitada" },
  ];

  const permanencyTypeOptions = [
    { id: "HOUR", label: "Horas" },
    { id: "MINUTE", label: "Minutos" },
    { id: "DAY", label: "Dias" },
  ];

  const populateAreaData = (
    data: CreateAreaReworkPayload[],
    pageNumber: number,
    routeId?: string,
    isPagination = false
  ) => {

    // Primeiro ordena tudo por ID decrescente
    const sortedById = [...data].sort((a, b) => {
      const idA = a.id || 0;
      const idB = b.id || 0;
      return idB - idA;
    });

    // Depois separa selecionados e não selecionados
    const selected = sortedById.filter(item => item.selected === true);
    const notSelected = sortedById.filter(item => item.selected !== true);

    // Ordena os selecionados por ID decrescente
    const sortedSelected = selected.sort((a, b) => {
      const idA = a.id || 0;
      const idB = b.id || 0;
      return idB - idA;
    });

    // Combina: selecionados primeiro, depois não selecionados
    const sortedData = [...sortedSelected, ...notSelected];

    setAreas(sortedData);
    setPages(Math.ceil(data.length / 10));

    if (routeId) {
      const allAreas = data || [];
      const reallySelected = allAreas.filter((area) => area.selected === true);

      if (reallySelected.length > 0) {
        const ids = reallySelected.map((area) => String(area.id));
        const cache = Object.fromEntries(reallySelected.map((area) => [String(area.id), area]));
        setPreSelectedAreaIds((prev) => [...new Set([...prev, ...ids])]);
        setPreSelectedAreaCache((prev) => ({ ...prev, ...cache }));
        setSelectedAreaCache((prev) => ({ ...prev, ...cache }));
      }
    }
  };

  const initialDataLoad = async (
    pageToSearch = 1,
    signal?: AbortSignal,
    routeId?: string,
    selectedAreaIds?: (string | number)[],
    forceRefresh = false
  ) => {
    if (isLoadingDataRef.current) {
      return;
    }

    isLoadingDataRef.current = true;

    try {
      if (!forceRefresh && initialData && initialData.length > 0 && pageToSearch === 1 && !hasInitialLoadRef.current) {
        populateAreaData(initialData, pageToSearch, routeId);
        hasInitialLoadRef.current = true;
        if (onDataUpdate) {
          onDataUpdate(initialData, pages);
        }
        return;
      }

      if (!forceRefresh && areasRework.length > 0 && pageToSearch === 1 && hasInitialLoadRef.current) {
        populateAreaData(areasRework, pageToSearch, routeId);
        return;
      }

      const res = await getAllAreas(debouncedSearch, pageToSearch, SIZE_ITEMS, "name,asc", routeId, selectedAreaIds);

      if (!signal?.aborted) {
        populateAreaData(res.items, pageToSearch, routeId);
        if (onDataUpdate) {
          onDataUpdate(res.items, res.pages);
        }
      }
    } catch (error) {
      console.error("[DynamicContentArea] Erro no initialDataLoad:", error);
      if ((error as any).name !== "CanceledError") {
        Notification.error("Não foi possível buscar as áreas.");
      }
    } finally {
      setTimeout(() => {
        isLoadingDataRef.current = false;
      }, 100);
    }
  };

  const filteredAreas = areas.filter((area) => area.name.toLowerCase().includes(search.toLowerCase()));

  const handleTabChange = (tabKey: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("tab", tabKey);
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const getSelectedClass = (isSelected: boolean) => (isSelected ? "" : "");

  const formatCoordinates = (coords: ICoordinates): string => {
    const format = (value: number, isLat: boolean): string => {
      const abs = Math.abs(value).toFixed(4);
      const dir = isLat ? (value >= 0 ? "N" : "S") : value >= 0 ? "E" : "W";
      return `${abs}° ${dir}`;
    };

    return `${format(coords.lat, true)} - ${format(coords.lng, false)}`;
  };

  const handleClearDrawing = () => {
    if (currentDrawingArea) {
      const currentFormatted = avoidAreas?.formatted?.split("|").filter(Boolean) ?? [];
      const permanentAreas = currentFormatted.filter((area) => area !== currentDrawingArea);
      const finalFormatted = permanentAreas.join("|");

      updateAvoidAreasStore(finalFormatted);
      setCurrentDrawingArea("");
    }

    setCircleDrawing(undefined);
    setPolygonDrawing(undefined);

    setDrawingPoints([]);
    setCenterMarker(null);
    setFormattedAddress("");
    setManualAvoidFormatted([]);
    handleChangeRadius(500);
  };

  const handleAreaCheckboxChange = (areaId: string, checked: boolean) => {
    changeAreaCheckbox(areas, areaId, checked);
  };

  const changeAreaCheckbox = useCallback(
    (areas: CreateAreaReworkPayload[], areaId: string, checked: boolean) => {
      const area = areas.find((a) => (a.id ?? "").toString() === areaId);
      if (!area) return;
      hasUserInteractedRef.current = true;

      if (checked) {
        setUserSelectedIds((prev) => [...new Set([...prev, areaId])]);
        setUserDeselectedIds((prev) => prev.filter((id) => id !== areaId));
      } else {
        setUserDeselectedIds((prev) => [...new Set([...prev, areaId])]);
        setUserSelectedIds((prev) => prev.filter((id) => id !== areaId));
      }

      setSelectedAreaIds((prev) => {
        const updatedIds = checked ? [...new Set([...prev, areaId])] : prev.filter((id) => id !== areaId);
        dispatch(defineAreasSelectedIds(updatedIds));
        return updatedIds;
      });

      setSelectedAreaCache((prev) => {
        if (checked) {
          return { ...prev, [areaId]: area };
        } else {
          const { [areaId]: _, ...rest } = prev;
          return rest;
        }
      });

      const selecteds = areasRework
      let updatedAreasRework: CreateAreaReworkPayload[];

      if (checked) {
        const selectedAreas = selecteds.some((storeArea) => String(storeArea.id) === areaId);

        if (!selectedAreas) {

          const areaToAdd = { ...area, selected: true };
          updatedAreasRework = [...selecteds, areaToAdd];

        } else {

          updatedAreasRework = selecteds.map((storeArea) =>
            String(storeArea.id) === areaId ? { ...storeArea, selected: true } : storeArea
          );

        }
      } else {
        updatedAreasRework = selecteds.filter((storeArea) => String(storeArea.id) !== areaId);
      }

      dispatch(defineAreasRework(updatedAreasRework));
      dispatch(defineAreas(updatedAreasRework));

      if (area.interestType === "AVOID") {
        const allAvoidAreas = updatedAreasRework.filter((a) => a.interestType === "AVOID");

        const allAvoidFormatted = allAvoidAreas.map((avoidArea) => {
          const isCircle = avoidArea.circleRadius !== null && avoidArea.circleRadius !== undefined;

          if (isCircle) {
            const center = avoidArea.points[0];
            const radius = avoidArea.circleRadius!;
            const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
            return `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;
          }

          return `polygon:${avoidArea.points.map((p) => `${p.lat},${p.lng}`).join(";")}`;
        });

        const finalFormatted = allAvoidFormatted.join("|");
        dispatch(defineAvoidAreas({ formatted: finalFormatted || undefined }));
      }

      lastProcessedAreasRef.current = "";
    },
    [areasRework, dispatch]
  );

  const validateAreaInputs = () => {
    if (!areaName || drawingPoints.length === 0 || !shape || !type) {
      Notification.warning("Preencha todos os campos obrigatórios.");
      return false;
    }

    if (shape === "POLYGON" && drawingPoints.length < 3) {
      Notification.warning("O polígono deve conter no mínimo 3 pontos.");
      return false;
    }

    if (radius <= 0) {
      Notification.warning("O raio deve ser maior que zero.");
      return false;
    }

    return true;
  };

  const buildDefaultTimes = () => ({
    velocityMin: minKmInputValue ? parseInt(minKmInputValue, 10) : 0,
    velocityMax: maxKmInputValue ? parseInt(maxKmInputValue, 10) : 0,
    stayTimeMin: minTimeInputValue ? parseInt(minTimeInputValue, 10) : 0,
    stayTimeMax: maxTimeInputValue ? parseInt(maxTimeInputValue, 10) : 0,
    stayTimeTypeMin: selectedMinPermanencyTimeOption || "MINUTE",
    stayTimeTypeMax: selectedMaxPermanencyTimeOption || "MINUTE",
  });

  const buildPayload = (): CreateAreaReworkPayload | null => {
    const areaTypeId = shape === "POLYGON" ? 1 : 2;

    const commonPayload = {
      name: areaName,
      narration: areaNarration || null,
      areaTypeId,
      interestType: type as "AVOID" | "INTEREST",
      points: drawingPoints,
      scope: selectedOption === 2 ? "GLOBAL" : "OPCIONAL",
    };

    const defaultTimes = buildDefaultTimes();

    if (shape === "CIRCLE") {
      return { ...commonPayload, circleRadius: radius, ...defaultTimes };
    }
    if (shape === "POLYGON") {
      return { ...commonPayload, ...defaultTimes };
    }

    Notification.error("Formato da área inválido.");
    return null;
  };

  const handleSaveArea = async () => {
    try {
      if (!validateAreaInputs()) return;

      const payload = buildPayload();
      if (!payload) return;

      const response = await createAreaRework(payload);
      const newAreaId = response?.[0]?.id;
      if (!newAreaId) return;

      setCreatedAreaId(newAreaId);
      setSelectedAreaIds((prev) => [...prev, newAreaId.toString()]);
      setUserSelectedIds((prev) => [...prev, newAreaId.toString()]);
      setUserDeselectedIds((prev) => prev.filter((id) => id !== newAreaId.toString()));

      const allSelectedIdsArray: number[] = [...userSelectedIds.map(Number), newAreaId].filter(
        (id, index, self) => self.indexOf(id) === index
      );

      try {
        await initialDataLoad(1, undefined, undefined, allSelectedIdsArray, true);
      } catch (error) {
        console.log("Erro ao buscar areas", error);
      }

      if (currentDrawingArea) {
        const currentFormatted = avoidAreas?.formatted?.split("|").filter(Boolean) ?? [];
        const permanentAreas = currentFormatted.filter((area) => area !== currentDrawingArea);
        updateAvoidAreasStore(permanentAreas.join("|"));
        setCurrentDrawingArea("");
      }

      changeAreaCheckbox(response, String(newAreaId), true);

      Notification.success("Área criada com sucesso!");
      handleClearDrawing();
      setAreaName("");
      setAreaNarration("");
      onCancel(true);
    } catch (error) {
      Notification.error("Erro ao criar a área.");
    }
  };

  useEffect(() => {
    if (!createdAreaId || areas.length === 0) return;

    const exists = areas.some((area) => area.id === createdAreaId);
    if (exists) {
      setSelectedAreaIds((prev) => {
        const updated = [...new Set([...prev, String(createdAreaId)])];
        dispatch(defineAreasSelectedIds(updated));
        return updated;
      });

      setCreatedAreaId(null);
    }
  }, [createdAreaId, areas, dispatch]);

  const getMarkerConfig = (type: string): {iconName: string, text: string, color: MarkerColor} => ({
    iconName: type === "AVOID" ? "rioglyph-error-sign" : "rioglyph-heart",
    text: type === "AVOID" ? "Área a ser evitada" : "Área de interesse",
    color: type === "AVOID" ? "bg-map-marker-danger" : "bg-map-marker-success"
  });

  const createMarkerName = (config: ReturnType<typeof getMarkerConfig>, coordinates: any) => (
    <div className="display-flex align-items-center gap-10 padding-3 margin-x-3">
      <span className={`text-anchorSize-h4 ${config.iconName}`} />
      <div className="line-height-16">
        <div className="text-medium">{config.text}</div>
        <div className="text-anchorSize-12">{formatCoordinates(coordinates)}</div>
      </div>
    </div>
  );

  const createMarker = (id: string, position: ICoordinates, config: ReturnType<typeof getMarkerConfig>): ISandboxMarker => ({
    id,
    position,
    type: "addressType",
    markerProps: {
      iconNames: [config.iconName],
      name: createMarkerName(config, position),
      markerColor: config.color,
      markerOnHover: true,
      anchorSize: "lg",
    },
  });

  const createCircleMarkers = (drawingPoints: ICoordinates[], type: string): ISandboxMarker[] => {
    if (drawingPoints.length === 0) return [];

    const config = getMarkerConfig(type);
    const lastPoint = drawingPoints[drawingPoints.length - 1];

    return [createMarker("circle-center-marker", lastPoint, config)];
  };

  const createPolygonMarkers = (drawingPoints: ICoordinates[], type: string): ISandboxMarker[] => {
    if (drawingPoints.length === 0) return [];

    const config = getMarkerConfig(type);

    return drawingPoints.map((point, index) =>
      createMarker(`polygon-point-${index}`, point, config)
    );
  };
  
  useEffect(() => {
    let markers: ISandboxMarker[] = [];

    if (drawingPoints.length > 0) {
      if (drawingType === "CIRCLE") {
        markers = createCircleMarkers(drawingPoints, type);
      } else if (drawingType === "POLYGON") {
        markers = createPolygonMarkers(drawingPoints, type);
      }
    }

    setDrawingMarkers(markers);
  }, [drawingType, drawingPoints, type]);

  useEffect(() => {
    setEnableDrawingClick(true);

    return () => {
      setEnableDrawingClick(false);
    };
  }, []);

  useEffect(() => {
    const shouldHandle = type === "AVOID";

    if (shape === "POLYGON" && drawingPoints.length < 3) {
      setManualAvoidFormatted([]);
      setCurrentDrawingArea("");
      return;
    }

    let newFormatted = "";

    if (shouldHandle && shape === "CIRCLE" && circleDrawing) {
      const { center, radius } = circleDrawing;
      const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
      newFormatted = `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;

      setManualAvoidFormatted((prev) => {
        const withoutCurrentDrawing = currentDrawingArea ? prev.filter((item) => item !== currentDrawingArea) : prev;
        return [...withoutCurrentDrawing, newFormatted];
      });

      setCurrentDrawingArea(newFormatted);
      return;
    }

    if (shouldHandle && shape === "POLYGON" && polygonDrawing) {
      newFormatted = `polygon:${polygonDrawing.points.map((p) => `${p.lat},${p.lng}`).join(";")}`;

      setManualAvoidFormatted((prev) => {
        const withoutCurrentDrawing = currentDrawingArea ? prev.filter((item) => item !== currentDrawingArea) : prev;
        return [...withoutCurrentDrawing, newFormatted];
      });

      setCurrentDrawingArea(newFormatted);
    }
  }, [circleDrawing, polygonDrawing, type, shape]);

  const mountDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const avoidAreasStoreDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastAvoidAreasFormatted = useRef<string>("");

  const updateAvoidAreasStore = (finalFormatted: string) => {
    if (lastAvoidAreasFormatted.current === finalFormatted) {
      return;
    }

    if (avoidAreasStoreDebounceRef.current) {
      clearTimeout(avoidAreasStoreDebounceRef.current);
    }

    avoidAreasStoreDebounceRef.current = setTimeout(() => {
      lastAvoidAreasFormatted.current = finalFormatted;
      dispatch(defineAvoidAreas({ formatted: finalFormatted || undefined }));
      avoidAreasStoreDebounceRef.current = null;
    }, 500);
  };

  const sortedIds = (arr: string[]) => {
    return arr.sort((a, b) => a.localeCompare(b));
  };

  useEffect(() => {
    if (!hasUserInteractedRef.current && hasComponentInitializedRef.current) {
      return;
    }

    if (isUpdatingAreasRef.current) {
      return;
    }

    if (!hasComponentInitializedRef.current) {
      hasComponentInitializedRef.current = true;
      if (!hasUserInteractedRef.current) {
        return;
      }
    }

    if (mountDebounceRef.current) {
      clearTimeout(mountDebounceRef.current);
    }

    mountDebounceRef.current = setTimeout(() => {
      if (avoidAreasStoreDebounceRef.current) {
        clearTimeout(avoidAreasStoreDebounceRef.current);
        avoidAreasStoreDebounceRef.current = null;
      }

      isUpdatingAreasRef.current = true;

      const resetFlag = () => {
        isUpdatingAreasRef.current = false;
      };

      const allAreaIds = [...new Set([...preSelectedAreaIds, ...userSelectedIds, ...selectedAreaIds])].filter(
        (id) => !userDeselectedIds.includes(id)
      );

      const allSelectedCache = { ...preSelectedAreaCache, ...selectedAreaCache };

      const currentAreasSignature = JSON.stringify({
        ids: sortedIds(allAreaIds),
        userSelected: userSelectedIds.sort(),
        userDeselected: userDeselectedIds.sort(),
      });

      if (lastProcessedAreasRef.current === currentAreasSignature) {
        resetFlag();
        return;
      }

      lastProcessedAreasRef.current = currentAreasSignature;

      const selectedAreas = allAreaIds
        .map((id) => allSelectedCache[id])
        .filter((area): area is CreateAreaReworkPayload => {
          if (!area) return false;

          const areaId = String(area.id);

          const isUserSelected = userSelectedIds.includes(areaId);
          const isUserDeselected = userDeselectedIds.includes(areaId);
          const isSelected = area.selected === true;
          const isPreSelected = preSelectedAreaIds.includes(areaId);

          if (isUserDeselected) {
            return false;
          }

          if (isUserSelected) {
            return true;
          }

          if (isSelected) {
            return true;
          }

          if (isPreSelected) {
            return true;
          }

          return false;
        });

      if (selectedAreas.length === 0 && hasUserInteractedRef.current) {
        const hasPreSelected = preSelectedAreaIds.length > 0 || areasSelectedIdsFromStore.length > 0;

        if (!hasPreSelected) {
          // dispatch(defineAreasRework([]));
          dispatch(defineAreas([]));
          setCheckboxAvoidFormatted([]);
          updateAvoidAreasStore("");
        }

        setTimeout(resetFlag, 0);
        return;
      }

      if (selectedAreas.length === 0 && !hasUserInteractedRef.current) {
        setTimeout(resetFlag, 0);
        return;
      }

      const interestAreas = selectedAreas
        .filter((area) => area.interestType === "INTEREST")
        .map((area) => ({
          ...area,
          velocityMin: cleanNumeric(area.velocityMin),
          velocityMax: cleanNumeric(area.velocityMax),
          stayTimeMin: cleanNumeric(area.stayTimeMin),
          stayTimeMax: cleanNumeric(area.stayTimeMax),
        }));

      const avoidAreas = selectedAreas
        .filter((area) => area.interestType === "AVOID")
        .map((area) => ({
          ...area,
          velocityMin: cleanNumeric(area.velocityMin),
          velocityMax: cleanNumeric(area.velocityMax),
          stayTimeMin: cleanNumeric(area.stayTimeMin),
          stayTimeMax: cleanNumeric(area.stayTimeMax),
        }));

      const allSelectedAreasForStore = [...interestAreas, ...avoidAreas];

      // dispatch(defineAreasRework(allSelectedAreasForStore));
      dispatch(defineAreas(allSelectedAreasForStore));

      const checkboxFormatted = avoidAreas.map((area) => {
        const isCircle = area.circleRadius !== null && area.circleRadius !== undefined;

        if (isCircle) {
          const center = area.points[0];
          const radius = area.circleRadius!;
          const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
          return `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;
        }

        return formatPolygon(area.points);
      });

      setCheckboxAvoidFormatted(checkboxFormatted);

      if (checkboxFormatted.length > 0 || manualAvoidFormatted.length > 0) {
        const allAvoidFormatted = [...new Set([...checkboxFormatted, ...manualAvoidFormatted])].filter(Boolean);
        const finalAvoidFormatted = allAvoidFormatted.join("|");
        updateAvoidAreasStore(finalAvoidFormatted);
      }

      setTimeout(resetFlag, 0);
    }, 300);
  }, [
    selectedAreaIds,
    selectedAreaCache,
    preSelectedAreaIds,
    preSelectedAreaCache,
    userSelectedIds,
    userDeselectedIds,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      if (mountDebounceRef.current) {
        clearTimeout(mountDebounceRef.current);
      }
      if (avoidAreasStoreDebounceRef.current) {
        clearTimeout(avoidAreasStoreDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedAreaIds.length > 0 && hasComponentInitializedRef.current) {
      if (!hasUserInteractedRef.current) {
        hasUserInteractedRef.current = true;
      }
    }
  }, [selectedAreaIds.length]);

  useEffect(() => {
    if (!hasInitialLoadRef.current && areas.length === 0) {
      hasInitialLoadRef.current = true;

      if (initialData && initialData.length > 0) {
        populateAreaData(initialData, 1, routeId ?? undefined);
        if (onDataUpdate) {
          onDataUpdate(initialData, pages);
        }

        if (areasSelectedIdsFromStore.length > 0) {
          hasUserInteractedRef.current = true;
        }
        return;
      }

      if (areasRework.length > 0) {
        populateAreaData(areasRework, 1, routeId ?? undefined);
        hasUserInteractedRef.current = true;
        return;
      }

      const controller = new AbortController();
      initialDataLoad(1, controller.signal, routeId ?? undefined);

      return () => controller.abort();
    }
  }, []);

  useEffect(() => {
    if (hasInitialLoadRef.current && previousPage !== page && previousPage !== 0 && !isPaginatorChangeRef.current) {
      const controller = new AbortController();
      const consolidatedIds = getConsolidatedSelectedIds();
      initialDataLoad(page, controller.signal, routeId ?? undefined, consolidatedIds);
      return () => controller.abort();
    }

    if (isPaginatorChangeRef.current) {
      isPaginatorChangeRef.current = false;
    }
  }, [page, getConsolidatedSelectedIds]);

  const previousSearchRef = useRef<string>("");

  useEffect(() => {
    if (!hasInitialLoadRef.current) return;

    previousSearchRef.current = debouncedSearch;
    const controller = new AbortController();
    setPage(1);

    async function fetchData() {
      const res = await getAllAreas(debouncedSearch, 1, SIZE_ITEMS, "name,asc", routeId || undefined);
      populateAreaData(res.items, 1, routeId || undefined);
      if (onDataUpdate) {
        onDataUpdate(res.items, res.pages);
      }
    }
    fetchData();
    return () => controller.abort();
  }, [debouncedSearch]);

  useEffect(() => {
    if (areasSelectedIdsFromStore.length > 0) {
      const reallySelectedAreas = areasRework.filter(
        (area) => area.selected === true && areasSelectedIdsFromStore.includes(String(area.id))
      );

      if (reallySelectedAreas.length > 0) {
        const validIds = reallySelectedAreas.map((area) => String(area.id));

        setPreSelectedAreaIds((prev) => {
          const newIds = [...new Set([...prev, ...validIds])];
          const prevSort = sortStr([...prev])
          const newSort = sortStr([...newIds]);
          if (JSON.stringify(prevSort) === JSON.stringify(newSort)) {
            return prev;
          }
          return newIds;
        });

        if (userSelectedIds.length === 0 && userDeselectedIds.length === 0) {
          setSelectedAreaIds((prev) => {
            const newIds = [...new Set([...prev, ...validIds])];
            const prevSort = sortStr([...prev]);
            const newSort = sortStr([...newIds]);
            if (JSON.stringify(prevSort) === JSON.stringify(newSort)) {
              return prev;
            }
            return newIds;
          });
        }
      }
    }
  }, [areasSelectedIdsFromStore, areasRework, userSelectedIds.length, userDeselectedIds.length]);

  useEffect(() => {
    if (!hasInitialLoadRef.current || areas.length === 0) return;
    if (areasRework.length > 0) {
      const reallySelectedAreas = areasRework.filter((area) => area.selected === true);

      if (reallySelectedAreas.length > 0) {
        const preSelectedIds = reallySelectedAreas.map((area) => String(area.id));
        const cache = Object.fromEntries(reallySelectedAreas.map((area) => [String(area.id), area]));

        setPreSelectedAreaIds((prev) => {
          const newIds = [...new Set([...prev, ...preSelectedIds])];
          const prevSort = sortStr([...prev]);
          const newSort = sortStr([...newIds]);
          if (JSON.stringify(prevSort) !== JSON.stringify(newSort)) {
            return newIds;
          }
          return prev;
        });

        setPreSelectedAreaCache((prev) => ({ ...prev, ...cache }));

        if (userSelectedIds.length === 0 && userDeselectedIds.length === 0) {
          setSelectedAreaCache((prev) => ({ ...prev, ...cache }));
        }
      }
    }
  }, [areasRework, hasInitialLoadRef.current, areas.length]);

  useEffect(() => {
    if (expectedPageData && isPaginationLoading) {
      const dataMatches = areas.length === expectedPageData.data.length;
      const pageMatches = page === expectedPageData.page;
      const itemsMatch = areas.every((area, index) => area.id === expectedPageData.data[index]?.id);

      if (dataMatches && pageMatches && itemsMatch) {
        setIsPaginationLoading(false);
        setExpectedPageData(null);
      }
    }
  }, [areas, page, expectedPageData, isPaginationLoading]);

  useEffect(() => {
    const allAreaIds = [...new Set([...preSelectedAreaIds, ...userSelectedIds, ...selectedAreaIds])].filter(
      (id) => !userDeselectedIds.includes(id)
    );

    if (allAreaIds.length > 0) {
      setTimeout(() => {
        dispatch(defineAreasSelectedIds(allAreaIds));
      }, 100);
    }
  }, [page, preSelectedAreaIds, userSelectedIds, userDeselectedIds, selectedAreaIds, dispatch]);

  const getAreaSelectionState = (areaId: string, area: CreateAreaReworkPayload) => {
    if (userSelectedIds.includes(areaId)) return true;
    if (userDeselectedIds.includes(areaId)) return false;
    if (preSelectedAreaIds.includes(areaId) || area.selected === true) return true;
    return false;
  };

  useEffect(() => {
    const formatUnit = (value: string, unit: string): string => {
      const numericValue = parseInt(value.replace(/\D/g, ""), 10);
      if (isNaN(numericValue)) return "";
      return `${numericValue} ${unit === "HOUR" ? "horas" : unit === "MINUTE" ? "minutos" : "dias"}`;
    };

    if (minTimeInputValue !== "") {
      setMinTimeInputValue(formatUnit(minTimeInputValue, selectedMinPermanencyTimeOption));
    }

    if (maxTimeInputValue !== "") {
      setMaxTimeInputValue(formatUnit(maxTimeInputValue, selectedMaxPermanencyTimeOption));
    }
  }, [selectedMinPermanencyTimeOption, selectedMaxPermanencyTimeOption]);

  useEffect(() => {
    if (!searchParams.get("tab")) {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("tab", "1");
      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { replace: true }
      );
    }
  }, []);

  const cleanCurrentDrawingArea = () => {
    if (!currentDrawingArea) return;

    const currentFormatted = avoidAreas?.formatted?.split("|").filter(Boolean) ?? [];
    const permanentAreas = currentFormatted.filter((area) => area !== currentDrawingArea);

    if (permanentAreas.length === currentFormatted.length) return;

    updateAvoidAreasStore(permanentAreas.join("|"));
    setCurrentDrawingArea("");
  };

  const buildNewDrawing = () => {
    const hasCircle = drawingType === "CIRCLE" && circleDrawing;
    const hasPolygon = drawingType === "POLYGON" && polygonDrawing;

    if (hasCircle) {
      const { center, radius } = circleDrawing;
      const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
      return formatCircle(center, offset, radius);
    }

    if (hasPolygon && polygonDrawing.points.length >= 3) {
      return formatPolygon(polygonDrawing.points);
    }

    return "";
  };

  const updateStoreWithDrawing = (newDrawingFormatted: string) => {
    const currentFormatted = avoidAreas?.formatted?.split("|").filter(Boolean) ?? [];

    const areasWithoutOldDrawing = currentDrawingArea
      ? currentFormatted.filter((area) => area !== currentDrawingArea)
      : currentFormatted;

    const finalFormatted = [...areasWithoutOldDrawing, newDrawingFormatted].join("|");

    updateAvoidAreasStore(finalFormatted);
    setCurrentDrawingArea(newDrawingFormatted);
  };

  useEffect(() => {
    if (type !== "AVOID") {
      cleanCurrentDrawingArea();
      return;
    }

    if (!hasUserInteractedRef.current) return;

    const newDrawingFormatted = buildNewDrawing();
    if (!newDrawingFormatted) return;

    updateStoreWithDrawing(newDrawingFormatted);
  }, [drawingType, circleDrawing, polygonDrawing, type, currentDrawingArea]);


  const handleNewPage = (params: { page: number; pages?: number }) => {
    const newPage = params.page > 1 ? params.page : 1;
    if (newPage > 1) {
      setPage(newPage);
      setController(prev => {
        return {
          start: 1,
          end: newPage * 10,
        }
      })
      return;
    }

    setPage(1);
    setController({
      start: 1,
      end: 10,
    })
  };

  const handleDrawingPoints = (index: number) => {
    hasUserInteractedRef.current = true;
    setDrawingPoints((prev) => {
      return prev.filter((_, i) => i !== index);
    });
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "1":
        return (
          <>
            <div className=" display-flex flex-column margin-top-15">
              <div className="">
                <label htmlFor="defaultInput" className="display-flex flex-wrap justify-content-between">
                  Nome
                </label>
                <input
                  id="defaultInput"
                  type="text"
                  className="form-control"
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                />
              </div>

              <div className="margin-top-15">
                <label htmlFor="narrationInput" className="display-flex flex-wrap justify-content-between">
                  Frase de narração
                </label>
                <textarea
                  id="narrationInput"
                  className="form-control"
                  rows={2}
                  placeholder="Frase que será narrada no app ao entrar na área"
                  value={areaNarration}
                  onChange={(e) => setAreaNarration(e.target.value)}
                />
              </div>

              <div className="display-flex gap-20 margin-top-25">
                <div className="width-50pct">
                  <label htmlFor="select">Formato</label>
                  <CustomInputSelect
                    className="display-block margin-bottom-5"
                    options={shapeOptions}
                    value={shape}
                    onChange={(selectedValue) => {
                      const type = String(selectedValue).toUpperCase();

                      if (type === "CIRCLE" || type === "POLYGON") {
                        setShape(type);
                        setDrawingType(type);
                      } else {
                        setShape("");
                        setDrawingType(null);
                      }
                      setDrawingPoints([]);
                      setCenterMarker(null);
                      setCircleDrawing(undefined);
                      setPolygonDrawing(undefined);
                      setFormattedAddress("");
                      handleChangeRadius(500);
                    }}
                  />
                </div>
                <div className="width-50pct">
                  <label htmlFor="select">Tipo</label>
                  <CustomInputSelect
                    className="display-block margin-bottom-5"
                    options={typeOptions}
                    value={type}
                    onChange={(selectedValue) => {
                      setType(String(selectedValue));
                      setDrawingPoints([]);
                      setCenterMarker(null);
                      setCircleDrawing(undefined);
                      setPolygonDrawing(undefined);
                      setFormattedAddress("");
                      handleChangeRadius(500);
                    }}
                  />
                </div>
              </div>

              <div className="display-flex margin-top-25">
                <RadioButton
                  name="stackedRadioBoxes"
                  custom
                  className={`padding-5 rounded-top-left rounded-top-right no-border ${getSelectedClass(
                    selectedOption === 1
                  )}`}
                  onChange={() => setSelectedOption(1)}
                  checked={selectedOption === 1}
                >
                  <div className="radio-text-wrapper display-flex">
                    <span className="radio-text" />
                    <div className="margin-left-5 margin-right-5">
                      <div className="text-medium">Específicas</div>
                      <div className="text-color-dark">
                        Áreas de uso específicas são atribuídas somente a rotas específicas.
                      </div>
                    </div>
                  </div>
                </RadioButton>

                <RadioButton
                  name="stackedRadioBoxes"
                  custom
                  className={`padding-5 rounded-top-left rounded-top-right no-border ${getSelectedClass(
                    selectedOption === 2
                  )}`}
                  onChange={() => setSelectedOption(2)}
                  checked={selectedOption === 2}
                >
                  <div className="radio-text-wrapper display-flex">
                    <span className="radio-text" />
                    <div className="margin-left-5 margin-right-5">
                      <div className="text-medium">Todas as rotas</div>
                      <div className="text-color-dark">
                        Áreas definidas para uso global são atribuidas a todas as rotas.
                      </div>
                    </div>
                  </div>
                </RadioButton>
              </div>

              {type === "INTEREST" && (
                <>
                  <div className="display-flex gap-15 margin-top-15">
                    <div className="velocity-item">
                      <label htmlFor="areaName" className="dynamic-content-dialog-label">
                        Velocidade mínima
                      </label>
                      <input
                        type="text"
                        className="input"
                        value={minKmInputValue}
                        onChange={minKmHandlers.handleChange}
                        onBlur={minKmHandlers.handleBlur}
                        onKeyDown={minKmHandlers.handleKeyDown}
                      />
                    </div>
                    <div className="velocity-item">
                      <label htmlFor="areaName" className="dynamic-content-dialog-label">
                        Velocidade máxima
                      </label>
                      <input
                        type="text"
                        className={`input-base ${isMaxVelocity ? "input-error" : "input"} `}
                        value={maxKmInputValue}
                        onChange={maxKmHandlers.handleChange}
                        onBlur={maxKmHandlers.handleBlur}
                        onKeyDown={maxKmHandlers.handleKeyDown}
                        ref={kmInputRef}
                      />
                    </div>
                  </div>

                  <div className="display-flex gap-15 margin-top-25">
                    <div className="display-flex gap-5">
                      <div className="display-flex flex-column">
                        <label className="dynamic-content-dialog-label margin-bottom-5 width-100pct ">
                          Tempo de permanência mínimo
                        </label>
                        <input
                          type="text"
                          className="input width-100pct"
                          value={minTimeInputValue}
                          onChange={minTimeInputHandlers.handleChange}
                          onBlur={minTimeInputHandlers.handleBlur}
                          onKeyDown={minTimeInputHandlers.handleKeyDown}
                        />
                      </div>
                      <div className="align-self-end">
                        <Select
                          id="1"
                          placeholder={""}
                          value={[permanencyTypeOptions[0].id]}
                          options={permanencyTypeOptions}
                          onChange={(item) => {
                            setSelectedMinPermanencyTimeOption(item?.id ?? "");
                          }}
                          className="test-className width-100"
                          btnClassName="test-btnClassName"
                          dropdownClassName="test-dropdownClassName"
                        />
                      </div>
                    </div>
                    <div className="display-flex gap-5">
                      <div className="display-flex flex-column">
                        <label htmlFor="areaName" className="dynamic-content-dialog-label">
                          Tempo de permanencia máximo
                        </label>
                        <input
                          type="text"
                          className={`input-base ${isMaxTime ? "input-error" : "input"}`}
                          value={maxTimeInputValue}
                          onChange={maxTimeInputHandlers.handleChange}
                          onBlur={maxTimeInputHandlers.handleBlur}
                          onKeyDown={maxTimeInputHandlers.handleKeyDown}
                          ref={timeInputRef}
                        />
                      </div>
                      <div className="align-self-end ">
                        <Select
                          id="1"
                          placeholder=""
                          options={permanencyTypeOptions}
                          value={[permanencyTypeOptions[0].id]}
                          onChange={(item) => {
                            setSelectedMaxPermanencyTimeOption(item?.id ?? "");
                          }}
                          className="test-className width-100"
                          btnClassName="test-btnClassName"
                          dropdownClassName="test-dropdownClassName"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="margin-top-25">
                <label htmlFor="enderecoInput" className="display-flex flex-wrap">
                  {shape === "POLYGON" ? "Mapa do Centro" : "Endereço"}
                </label>
                <Autosuggest
                  fetchData={async (text: string) => {
                    const response = await fetchAutosuggest(text);
                    return response?.map((i) => ({ label: i.title, description: i.address.label, ...i } as any));
                  }}
                  placeholder="Informe o centro do mapa"
                  icon="rioglyph-finish"
                  defaultValue={formattedAddress}
                  onClear={() => {
                    hasUserInteractedRef.current = true;
                    handleClearDrawing();
                  }}
                  onSuggestionSelected={(e, i) => {
                    hasUserInteractedRef.current = true;
                    const coordinates = i.suggestion.position as unknown as ICoordinates;
                    if (shape === "CIRCLE") {
                      setCircleDrawing({
                        center: coordinates,
                        radius: radius,
                        type: type === "AVOID" ? "avoid" : "interest",
                      });
                      setDrawingPoints([coordinates]);
                    } else if (shape === "POLYGON") {
                      setDrawingPoints((prev) => [...prev, coordinates]);
                    }

                    setMapCenter(coordinates);
                    setCenterMarker(coordinates);
                    setFormattedAddress(formatCoordinates(coordinates));
                  }}
                />
              </div>
              {shape === "CIRCLE" && (
                <>
                  <div className="display-flex flex-column margin-top-25">
                    <label>Raio da área</label>
                    <div className="display-flex align-items-center">
                      <Slider
                        value={radius}
                        minValue={0}
                        maxValue={80000}
                        step={1}
                        onDragEnd={() => { }}
                        onChange={handleChangeRadius}
                      />
                      <div className="width-100 margin-left-10">
                        <input
                          className="form-control text-center no-controls"
                          type="text"
                          min={0}
                          max={80000}
                          value={`${radius} m`}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/\D/g, "");
                            const numeric = parseInt(cleaned, 10);
                            const parsed = isNaN(numeric) ? 0 : numeric;

                            handleChangeRadius(parsed);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className="display-flex align-items-center margin-top-5 padding-5"
                    style={{ backgroundColor: "#EEF4F9" }}
                  >
                    <div className="bg-gray width-15 height-15 display-flex align-items-center justify-content-center rounded-circle margin-right-10 margin-left-15">
                      <span className="rioglyph rioglyph-info text-size-12 text-color-white" />
                    </div>
                    <span className="text-color-gray">
                      Segurando e arrastando, movimente a posição do circulo no mapa{" "}
                    </span>
                  </div>
                </>
              )}
              {shape === "POLYGON" && (
                <>
                  <div
                    className="display-flex align-items-center margin-top-15 padding-5"
                    style={{ backgroundColor: "#EEF4F9" }}
                  >
                    <div className="bg-gray display-flex align-items-center justify-content-center rounded-circle margin-right-10 margin-left-15">
                      <span className="rioglyph rioglyph-info text-size-12 text-color-white padding-2" />
                    </div>
                    <span className="text-color-gray text-size-12 padding-right-5 margin-right-5">
                      Para criar o polígono, clique no mapa à volta do perímetro pretendido. Os novos pontos são sempre
                      adicionados à aresta mais próxima. Pode alterar a forma do polígono arrastando os pontos.
                    </span>
                  </div>
                  <div className="display-flex flex-column margin-top-25 padding-left-25">
                    <span className="text-color-gray text-size-12">Pontos Selecionados</span>

                    {drawingPoints.map((point, index) => (
                      <div key={index} className="display-flex align-items-center margin-top-5">
                        <span className="rioglyph rioglyph-layer text-color-darker text-size-20" />
                        <span className="text-color-darker text-size-14 margin-left-10">
                          {formatCoordinates(point)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-primary btn-link btn-icon-only margin-left-auto"
                          onClick={() => handleDrawingPoints(index)}
                        >
                          <span className="rioglyph rioglyph-trash" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <button
                type="button"
                className="btn btn-default width-20pct margin-top-20 align-self-end"
                onClick={() => {
                  hasUserInteractedRef.current = true;
                  handleClearDrawing();
                }}
              >
                <span className="" aria-hidden="true"></span>
                {shape === "CIRCLE" ? "Excluir ponto" : "Excluir pontos"}
              </button>
            </div>
            <div className="border border-style-solid border-color-gray border-width-1 border-top-only margin-top-25">
              <button type="button" className="btn btn-default width-100pct margin-top-25" onClick={handleSaveArea}>
                <span className="" aria-hidden="true"></span>
                <span className="text-normal">Salvar área</span>
              </button>
            </div>
          </>
        );
      case "2":
        return (
          <>
            <div className="margin-top-15">
              <TableSearch value={search} onChange={(value) => setSearch(value)} placeholder="Buscar em áreas" />
            </div>
            <div className="margin-top-15">
              <table className="table table-bordered table-layout-fixed table-head-filled">
                <thead>
                  <tr>
                    <th className="width-60">✓</th>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Uso</th>
                  </tr>
                </thead>
                <tbody>
                  {isPaginationLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={`loading-${index}`}>
                        <td>
                          <div
                            style={{
                              width: "20px",
                              height: "20px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "4px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          ></div>
                        </td>
                        <td>
                          <div
                            style={{
                              width: "80%",
                              height: "16px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "4px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          ></div>
                        </td>
                        <td>
                          <div
                            style={{
                              width: "60px",
                              height: "24px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "12px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          ></div>
                        </td>
                        <td>
                          <div
                            style={{
                              width: "70%",
                              height: "16px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "4px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          ></div>
                        </td>
                      </tr>
                    ))
                  ) : filteredAreas.length > 0 ? (
                    filteredAreas.slice(controller.start, controller.end).map((area, index) => (
                      <tr key={index}>
                        <td>
                          <Checkbox
                            checked={getAreaSelectionState((area.id ?? "").toString(), area)}
                            onChange={(e) => handleAreaCheckboxChange((area.id ?? "").toString(), e.target.checked)}
                            label=""
                          />
                        </td>
                        <td>
                          <span className="text-size-14 text-color-darker">{area.name}</span>
                        </td>
                        <td>
                          <ChipStatus status={area?.interestType} />
                        </td>
                        <td>
                          <span className="text-size-14 text-color-darker">{formatScope(area.scope || "")}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center padding-20">
                        <span className="text-color-gray">Nenhuma área encontrada</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
                <Paginator
                  handlePage={(newPage) => {
                    handleNewPage(newPage);
                  }}
                  page={page}
                  pages={pages}
                  pageSize={PAGE_SIZE}
                  disabled={filteredAreas.length == 0}
                  hiddenReset={false}
                />
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <div className="padding-20" style={{ marginTop: "-30px" }}>
        <SubNavigation
          className="padding-0 margin-0 border border-bottom-only border-width-1"
          navItems={[
            {
              key: "1",
              route: (
                <NavLink
                  style={{
                    padding: "10px 20px",
                    margin: 0,
                    textAlign: "center",
                  }}
                  to="?tab=1"
                  onClick={(e) => {
                    e.preventDefault();
                    handleTabChange("1");
                  }}
                  className={() => (searchParams.get("tab") === "1" ? "active" : "")}
                >
                  CRIAR ÁREA
                </NavLink>
              ),
            },
            {
              key: "2",
              route: (
                <NavLink
                  to="?tab=2"
                  style={{
                    padding: "10px 20px",
                    margin: 0,
                    textAlign: "center",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTabChange("2");
                  }}
                  className={() => (searchParams.get("tab") === "2" ? "active" : "")}
                >
                  SELECIONADAS
                </NavLink>
              ),
            },
          ]}
        />
        {renderTabContent()}
      </div>
    </>
  );
};

const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  if (prevProps.pages !== nextProps.pages) return false;
  if (prevProps.onCancel !== nextProps.onCancel) return false;
  if (prevProps.onDataUpdate !== nextProps.onDataUpdate) return false;

  const prevDataLength = prevProps.initialData?.length || 0;
  const nextDataLength = nextProps.initialData?.length || 0;

  if (prevDataLength !== nextDataLength) return false;

  if (prevDataLength > 0 && nextDataLength > 0) {
    const prevIds = formatAreaIds(prevProps?.initialData);
    const nextIds = formatAreaIds(nextProps?.initialData);

    if (prevIds !== nextIds) return false;
  }

  return true;
};

export default React.memo(DynamicContentArea, arePropsEqual);
